import classNames from 'classnames/bind'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Dialog, Loader, PercentButton } from '@percent/admin-dashboard/common/components'
import { ReactComponent as CheckMark } from '@percent/admin-dashboard/common/assets/images/check_mark.svg'
import { useMutation } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { PostActivityEventProps } from '@percent/admin-dashboard/api/types'
import { ReactComponent as AlertIcon } from '@percent/admin-dashboard/common/assets/images/alert.svg'
import { Box, Typography } from '@percent/lemonade'
import { SubmitModalProps } from './SubmitModal.types'
import styles from './SubmitModal.module.scss'
import { RiskOfRejection } from '@percent/admin-dashboard/common/hooks/useActivityTags/useActivityTags.types'

const cx = classNames.bind(styles)

export function SubmitModal({
  isOpened,
  setIsOpened,
  organisation,
  subTagsForm,
  isFlagged,
  tagsData,
  isNoneOfTheAbove
}: SubmitModalProps) {
  const { t } = useTranslation()
  const { eligibilityService } = useServices()
  const navigate = useNavigate()

  const [{ isLoading, errorMessage, success }, { apiFunc: submitFunc }] = useMutation(
    eligibilityService.submitActivityEvent
  )

  const handleSubmit = () => {
    submitFunc({
      organisationId: organisation?.id,
      subTags: subTagsForm && subTagsForm?.length > 0 ? subTagsForm : null
    } as PostActivityEventProps)
  }

  const handleFinishAction = () => {
    setIsOpened(false)
    navigate(`/organizations/${organisation?.id}`)
  }

  const getRiskOfRejectionText = (riskOfRejection: RiskOfRejection) => {
    if (!riskOfRejection) return null

    return riskOfRejection === 'medium'
      ? t('dialog.submitTags.mayCauseRejection')
      : t('dialog.submitTags.likelyToCauseRejection')
  }

  return (
    <Dialog
      openModal={isOpened}
      onClose={() => setIsOpened(false)}
      withoutHeader={success}
      headerTitle={t('dialog.submitTags.title')}
    >
      {isLoading ? (
        <Loader />
      ) : errorMessage ? (
        <Box className={styles.mainContainer}>{errorMessage}</Box>
      ) : success ? (
        <Box className={styles.dialogBody}>
          <Box className={styles.checkMark}>
            <CheckMark />
          </Box>
          <Typography variant="h2" className={styles.successTitle}>
            {t(isFlagged ? 'dialog.submitTags.haveBeenSubmitted' : 'dialog.submitTags.haveBeenAssigned')}
          </Typography>
          <PercentButton title={t('button.done')} onClick={handleFinishAction} className={styles.centerButton} />
        </Box>
      ) : (
        <>
          <Box className={styles.modalBody}>
            <Typography variant="large" className={styles.bodyText}>
              {isFlagged && t('dialog.submitTags.flagOrganisation')}
              {isNoneOfTheAbove && t('dialog.submitTags.noneOfAbove')}
              {!isFlagged && !isNoneOfTheAbove && t('dialog.submitTags.assignTags')}
              {!isNoneOfTheAbove && <strong> {organisation?.displayName ?? organisation?.name}.</strong>}
            </Typography>
          </Box>
          <Box className={styles.mainContainer}>
            <ul className={styles.tagsList}>
              {tagsData
                .filter(tag =>
                  tag.subTags
                    .map(subTag => subTag.id)
                    .some(subTagId => subTagsForm?.find(sub => sub.id === subTagId)?.meetsCriteria)
                )
                .map(tag => {
                  const selectedSubTags = tag.subTags.filter(
                    subTag => subTagsForm?.find(sub => sub.id === subTag.id)?.meetsCriteria
                  )
                  const riskOfRejection = selectedSubTags.find(subTag => subTag.metadata?.riskOfRejection)?.metadata
                    ?.riskOfRejection

                  return (
                    <li key={tag.id}>
                      <Box className={styles.tagTitle}>
                        <p>{tag.name}</p>
                      </Box>
                      <Box className={styles.subTagsContainer}>
                        {selectedSubTags.map(subTag => (
                          <Box
                            key={subTag.id}
                            className={cx({
                              subTag: true,
                              [`${subTag.metadata?.riskOfRejection}RiskOfRejection`]: subTag.metadata?.riskOfRejection
                            })}
                          >
                            {subTag.name}
                          </Box>
                        ))}
                      </Box>
                      {riskOfRejection && (
                        <Box
                          className={cx({
                            textSensitive: riskOfRejection === 'high',
                            textMediumRiskOfRejection: riskOfRejection === 'medium'
                          })}
                        >
                          <AlertIcon
                            className={cx({
                              alertIcon: riskOfRejection === 'high',
                              mediumRiskOfRejectionIcon: riskOfRejection === 'medium'
                            })}
                          />{' '}
                          {getRiskOfRejectionText(riskOfRejection)}
                        </Box>
                      )}
                    </li>
                  )
                })}
            </ul>
            <Box className={styles.submitTextWrapper}>
              <Typography variant="large" className={styles.submitText}>
                {t(isFlagged || isNoneOfTheAbove ? 'dialog.submitTags.continue' : 'dialog.submitTags.willBeSubmitted')}
              </Typography>
            </Box>
          </Box>
          <Box className={styles.dialogFooter}>
            <PercentButton title={t('button.submitTags')} onClick={handleSubmit} testId="submit-tags" />
            <PercentButton
              title={t('button.cancel')}
              onClick={() => setIsOpened(false)}
              textColour="secondaryTextColour"
              outlined
            />
          </Box>
        </>
      )}
    </Dialog>
  )
}
